import { useEffect, useRef, useState } from "react";
import { T } from "react-translator-component";
import {
  Box, ClickAwayListener, Grow, MenuItem, MenuList,
  Paper, Popper,
  Table, TableBody, TableCell, TableHead, TableRow,
  TextField, Typography,
} from "@mui/material";
import PeopleIcon from '@mui/icons-material/People';
import SortIcon from '@mui/icons-material/Sort';
import { colors } from "../../common/colors";
import LoadingModal from "../../components/LoadingModal";
import Separator from "../../components/Separator";
import { useAppDispatch } from "../../redux/store";
import AdminPageFrame from "../components/AdminPageFrame";
import ContainerPaper from "../components/ContainerPaper";
import GroupIcon from "../components/GroupIconImage";
import FetchButton from "../components/FetchButton";
import DisplayModal, { ModalType } from "../components/memberGroups/DisplayModal";
import ReadMore from "../components/ReadMore";
import { setErrorObj } from "../redux/errorReducer";
import apiServices from "../services/apiServices";
import { formatNumber } from "../util/util";

export default function MemberGroupList() {
  const dispatch = useAppDispatch();

  /* 検索項目 */
  const [query, setQuery] = useState<string>("");
  const [sortOption, setSortOption] = useState<"asc" | "desc">("asc");

  /* API関連 */
  const sortMenuOptions = [
    {value: "desc", name: T("mng_user_notifications_sort_desc")},
    {value: "asc", name: T("mng_user_notifications_sort_asc")},
  ];
  const [lastParams, setLastParams] = useState<any>();
  const [member_groups, setMemberGroups] = useState<any[]>([]);
  const [pagination, setPagination] = useState<any>({});

  /* 画面制御 */
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [modalType, setModalType] = useState<ModalType>();
  const [memberGroupId, setMemberGroupId] = useState<number>();
  const [openDateSortOption, setOpenDateSortOption] = useState<boolean>(false);
  const sortAnchorRef = useRef<any>(null)

  const handleSelectSort = async (value: "asc" | "desc") => {
    setSortOption(value)
    setOpenDateSortOption(false)
  }

  useEffect(() => {
    if (!lastParams) return;

    const f = async () => {
      setIsLoading(true);

      const params = {
        ...lastParams,
        sort_direction: sortOption,
        page: 1
      };
      await getMemberGroups(params);

      setIsLoading(false);
    }
    f();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortOption]);

  useEffect(() => {
    handleSearch();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* API関連 */
  const getMemberGroups = async (params: any) => {
    const _p = { ...params, sort_type: "name" };

    try {
      const res = await apiServices.getMemberGroupList(_p);
      if (res.pagination.current_page > 1) {
        setMemberGroups((prev: any[]) => [...prev, ...res.member_groups]);
      } else {
        setMemberGroups(res.member_groups);
      }
      setPagination(res.pagination);
      setLastParams(_p);
    } catch (error: any) {
      dispatch(setErrorObj(error));
    }
  };

  const handleSearch = async () => {
    setIsLoading(true);

    const params = {
      term: query,
      sort_direction: sortOption
    };
    await getMemberGroups(params);

    setIsLoading(false);

    return false;
  }

  const handleMore = async () => {
    if (!pagination.next_page || pagination.next_page < 1) return

    setIsLoading(true);

    const params = {
      ...lastParams,
      page: pagination.next_page
    };
    await getMemberGroups(params);

    setIsLoading(false);
  }

  /* イベント */
  const handleShowDetails = (item: any) => {
    setMemberGroupId(item.id);
    setModalType("Details");
  }

  const handleShowUsers = (e: any, item: any) => {
    e.stopPropagation();
    setMemberGroupId(item.id);
    setModalType("Users");
  }

  const handleCloseModal = () => {
    setModalType(undefined);
    setMemberGroupId(undefined);
  }

  return (
    <AdminPageFrame>
      <LoadingModal visible={isLoading}/>

      <ContainerPaper>
        <Typography sx={styles.search.title}>{T("mng_search_area_title")}</Typography>
        <Box sx={styles.search.inputs.container}>
          <TextField
            label={T("mng_member_groups_search_query_placeholder")}
            value={query}
            onChange={(e: any) => setQuery(e.target.value)}
            variant="outlined" sx={styles.search.inputs.query}
          />
          <FetchButton
            title={T('mng_search')}
            onSubmit={handleSearch}
          />
        </Box>
      </ContainerPaper>

      <ContainerPaper>
        <Box sx={styles.data.header}>
          <Typography sx={styles.data.title}>{T("mng_member_groups_data_title")}</Typography>
        </Box>

        <Separator color={colors.farm_gray100} />

        <Table aria-label="caption table" sx={styles.data.table.base}>
          <TableHead>
            <TableRow>
              <TableCell
                onClick={() => setOpenDateSortOption(true)}
                sx={styles.data.table.name}
              >
                {T("mng_member_groups_name")}({T(`mng_member_groups_sort_${sortOption}`)})
                <SortIcon sx={{color: colors.farm_blue200}} ref={sortAnchorRef}/>
              </TableCell>
              <TableCell sx={styles.data.table.usersCount}>{T("mng_member_groups_users_count")}</TableCell>
              <TableCell sx={styles.data.table.members}></TableCell>
            </TableRow>
          </TableHead>

          <TableBody sx={styles.data.table.body}>
            {member_groups.map((item: any, index: number) => (
              <TableRow key={index} onClick={() => handleShowDetails(item)}>
                <TableCell sx={styles.data.table.name}>
                  <GroupIcon groupId={item.id} alt={item.name} />{item.name}
                </TableCell>
                <TableCell sx={styles.data.table.usersCount}>
                  {formatNumber(item.member_group_users_count + 1)}
                </TableCell>
                <TableCell onClick={(e: any) => handleShowUsers(e, item)} sx={styles.data.table.members}>
                  <PeopleIcon />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <ReadMore open={pagination.next_page > 0} sx={styles.data.pagination} onClick={() => handleMore()} />
      </ContainerPaper>

      {modalType && memberGroupId && (
        <DisplayModal
          type={modalType}
          id={memberGroupId}
          onClose={handleCloseModal}
        />
      )}

      <Popper
        open={openDateSortOption}
        placement={'right-start'}
        anchorEl={sortAnchorRef.current}
        transition
      >
        {({ TransitionProps }) => (
          <Grow {...TransitionProps} style={{transformOrigin:'top'}}>
            <Paper>
              <ClickAwayListener onClickAway={() => setOpenDateSortOption(false)}>
                <MenuList>
                  {sortMenuOptions.map((option: any) => (
                    <MenuItem
                      key={option.value}
                      onClick={() => handleSelectSort(option.value)}
                    >
                      {option.name}
                    </MenuItem>
                  ))}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </AdminPageFrame>
  );
}

const styles = {
  search: {
    title: {
      paddingY: 2,
    },
    inputs: {
      container: {
        display: "flex",
        gap: 2
      },
      query: {
        flex: 1
      },
      status: {
        width: "10em"
      }
    }
  },
  data: {
    container: {
      padding: 2,
      margin: 3
    },
    header: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      padding: 2,
      lineHeight: "120px"
    },
    title: {
      fontWeight: 700,
      fontSize: 16,
    },
    table: {
      base: {
        tableLayout: "fixed"
      },
      body: {
        padding: 1
      },
      name: {
        width: "80%",
        textOverflow: "ellipsis",
        overflow: "hidden",
        whiteSpace: "nowrap",
        "& img": {
          width: "30px",
          height: "30px",
          borderRadius: "50%",
          objectFit: "cover",
          marginRight: 1
        }
      },
      usersCount: {
        width: "10%"
      },
      members: {
        width: "10%"
      }
    },
    pagination: {
      width: "100%"
    }
  },
  errorMessage: {
    margin: 0,
    fontFamily: "mplus-1c-regular"
  },
}